/* eslint-disable no-console */
import _get from 'lodash/get';
import _merge from 'lodash/merge';
import { Lightning } from '@lightningjs/sdk';
import Store from 'lightningjs-redux';
import { addHours, differenceInSeconds, startOfDay } from 'date-fns';
import { Block } from '../../../block/block.component';
import { getBlockStyle } from '../../../block/block.style';
import Square from '../../../square/square.component';
import { EPG_POPUP_ID, EPG_LAYOUT_POPUP_ID } from '../../../../popups/popups.constants';
import { showPopupAction } from '../../../../../logic/popup/popup.actions';
import { makeNotification, NOTIFICATION_LEVEL } from '../../../notifications';
import { EPG_SIGNAL, NOTIFICATION_SIGNAL, PORTION_REQUESTED_SIGNAL, SELECTED_SIGNAL } from '../../../signals';
import { t } from '../../../../i18n';
import { getTitle } from '../utils/block';
import { EpgBox } from './epgBox/epgBox.component';
import { EpgTimeline } from './epgTimeline/epgTimeline.component';
import { HEIGHT as TIMELINE_HEIGHT } from './epgTimeline/epgTimeline.style';
import { HEIGHT as NAV_HEIGHT } from './epgNav/epgNav.style';
import { EpgNav } from './epgNav/epgNav.component';
import {
  SIGNAL_GO_LEFT,
  SIGNAL_GO_RIGHT,
  SIGNAL_GO_TO_AFTERNOON,
  SIGNAL_GO_TO_EVENING,
  SIGNAL_GO_TO_LIVE,
  SIGNAL_GO_TO_MORNING,
  SIGNAL_OPEN_DATE_SELECTOR,
} from './epgNav/epgNav.constants';
import {
  getInitialStyle,
  CHANNEL_SIZE,
  defaultContext,
  GAP,
  RIGHT_PADDING,
  TIMELINE_MARGIN_BOTTOM,
  TIMELINE_MARGIN_TOP,
  MAX_VISIBLE_ROWS,
  EMPTY_VIEW,
} from './epg.style';
import {
  AFTERNOON_HOUR,
  EVENING_HOUR,
  FOCUS_MODE_GRID,
  FOCUS_MODE_NAV,
  MORNING_HOUR,
  PIXELS_PER_MINUTE,
  templates,
} from './epg.constants';
import { getChannelContent, isActionAllowed, offsetFromStart, trimEpg } from './epg.utils';
import { EpgGrid } from './epgGrid/epgGrid.component';
import { SET_X_SIGNAL, SET_Y_SIGNAL } from './epgGrid/epgGrid.constants';

export class HorizontalEpg extends Lightning.Component {
  static _template() {
    return _merge(
      {
        Nav: {
          type: EpgNav,
          signals: {
            [SIGNAL_GO_TO_MORNING]: '_goToMorning',
            [SIGNAL_GO_TO_AFTERNOON]: '_goToAfternoon',
            [SIGNAL_GO_TO_EVENING]: '_goToEvening',
            [SIGNAL_GO_TO_LIVE]: '_handleGoToLive',
            [SIGNAL_OPEN_DATE_SELECTOR]: '_openDateSelector',
            [SIGNAL_GO_LEFT]: '_goLeft',
            [SIGNAL_GO_RIGHT]: '_goRight',
          },
        },
        ChannelsVerticalClippingRoot: {
          clipping: true,
          Channels: {},
        },
        Grid: {
          clipping: true,
          Timeline: {
            type: EpgTimeline,
          },
          GridRoot: {
            type: EpgGrid,
            passSignals: {
              [PORTION_REQUESTED_SIGNAL]: true,
            },
            signals: {
              [SET_X_SIGNAL]: '_handleGridX',
              [SET_Y_SIGNAL]: '_handleGridY',
              [SELECTED_SIGNAL]: '_handleBoxSelected',
            },
          },
        },
        Title: {},
      },
      getInitialStyle(),
    );
  }

  static getOffsetsByBlockData(value) {
    const result = Block.getOffsetsByBlockData(value);
    const count = _get(value, 'data.content.items.length') || 0;
    const { screenSize } = value;

    return {
      ...result,
      h:
        Math.min(count, MAX_VISIBLE_ROWS[screenSize]) * (CHANNEL_SIZE[screenSize] + GAP) -
        GAP +
        TIMELINE_HEIGHT[screenSize] +
        NAV_HEIGHT[screenSize] +
        TIMELINE_MARGIN_BOTTOM +
        TIMELINE_MARGIN_TOP,
    };
  }

  _construct() {
    this._contentPadding = 0;
    this._style = defaultContext;
    this._focusMode = FOCUS_MODE_NAV;
  }

  _detach() {
    this._data = undefined;
    this._rowsData = null;
    clearTimeout(this._itemFocusTimeout);
    cancelAnimationFrame(this._animationFrameHandle);
  }

  get rightPadding() {
    return RIGHT_PADDING[this._style.screenSize];
  }

  get contentPadding() {
    return this._contentPadding;
  }

  set contentPadding(value) {
    if (this._contentPadding === value) {
      return;
    }

    this._contentPadding = value;

    if (!this._data) {
      return;
    }

    const x = this._contentPadding + CHANNEL_SIZE[this._style.screenSize] + GAP;
    this._gridWidth = this._data.rowSize.w - x;

    this.patch({
      ChannelsVerticalClippingRoot: {
        x: this._contentPadding,
      },
      Nav: {
        x: this._contentPadding,
        w: this._data.rowSize.w - this._contentPadding - this.rightPadding,
      },
      Grid: {
        x,
        w: this._gridWidth,
        GridRoot: {
          w: this._gridWidth,
        },
      },
      Title: { x: this._contentPadding },
    });

    this._goToLive();
  }

  get data() {
    return this._data;
  }

  set data(value) {
    if (!value || !value.block || !value.block.content) {
      this.patch(EMPTY_VIEW);

      return;
    }

    const { content, pages, id } = value.block;
    const rows = (content.items || []).filter(block =>
      Boolean(block.epgBox && block.epgBox.length && block.epgBox.some(Boolean)),
    );
    const currentDate = content.date;
    const text = getTitle(value.block.title);
    const hasTitle = Boolean(text);

    // TODO: use colorMode
    // eslint-disable-next-line no-unused-vars
    const { screenSize, colorMode } = this._style;
    const { items, title } = getBlockStyle({
      hasTitle,
      isFirstBlock: value.blockIndex === 0,
      screenSize,
    });

    const channelSize = CHANNEL_SIZE[this._style.screenSize];
    const x = this._contentPadding + channelSize + GAP;
    const w = value.rowSize.w - x;
    const rowsHeight = Math.min(rows.length, MAX_VISIBLE_ROWS[screenSize]) * (channelSize + GAP) - GAP;
    const timelineOuterHeight = TIMELINE_HEIGHT[screenSize] + TIMELINE_MARGIN_BOTTOM + TIMELINE_MARGIN_TOP;
    const h = rowsHeight + timelineOuterHeight;
    const channelConfig = { w: channelSize, h: channelSize };
    this._gridWidth = w;
    console.log(`[EPG] Setting grid width on data change to ${this._gridWidth}`);

    const firstItem = rows[0].epgBox.find(Boolean);
    const start = addHours(startOfDay(new Date(firstItem.start.date)), 6);
    const end = addHours(start, 24);

    const isUpdate = differenceInSeconds(this._start, start) <= 1;
    console.log(`[EPG] Is update: ${isUpdate}`);
    this._start = start;
    this._end = end;
    this._data = value;
    this._rowsData = rows.map(({ epgBox: box }, rowIndex) => {
      let offset = 0;
      const y = rowIndex * (channelSize + GAP);

      return {
        h: channelSize,
        y,
        children: trimEpg(box, start, end).map((content, itemIndex, collection) => {
          const { start, end } = content;
          const duration = Math.round(differenceInSeconds(end.date, start.date) / 60);
          const itemWidth = duration * PIXELS_PER_MINUTE;

          const item = {
            type: EpgBox,
            w: itemWidth,
            h: channelSize,
            x: offset,
            y,
            rowIndex,
            itemIndex,
            index: `${rowIndex}_${itemIndex}`,
            item: {
              content,
              itemWidth,
              itemHeight: channelSize,
              isLastItem: itemIndex === collection.length - 1,
              offset,
            },
          };

          offset += itemWidth;

          return item;
        }),
      };
    });

    const channels = this.tag('Channels');
    const currentChannelsCount = channels.children.length;
    for (let i = 0; i < Math.min(currentChannelsCount, rows.length); i += 1) {
      channels.children[i].item = {
        content: getChannelContent(rows[i].channel),
        config: channelConfig,
      };
    }

    const channelsToCreate = rows.length - currentChannelsCount;
    if (channelsToCreate > 0) {
      for (let i = 0; i < channelsToCreate; i += 1) {
        const channel = this.stage.c({
          type: Square,
          item: {
            content: getChannelContent(rows[currentChannelsCount + i].channel),
            config: channelConfig,
          },
          y: (currentChannelsCount + i) * (channelSize + GAP),
        });
        channels.childList.add(channel);
      }
    }

    this.patch({
      ChannelsVerticalClippingRoot: {
        x: this._contentPadding,
        y: items.y + NAV_HEIGHT[screenSize] + timelineOuterHeight,
        h: rowsHeight,
      },
      Nav: {
        y: items.y,
        date: currentDate,
      },
      Grid: {
        x,
        y: items.y + NAV_HEIGHT[screenSize],
        w,
        h,
        Timeline: {
          range: {
            start,
            end,
          },
        },
        GridRoot: {
          w,
          h: rowsHeight,
          rows: {
            id,
            pages,
            pagination: content.pagination,
            rows: this._rowsData,
            rowHeight: channelSize,
            start,
            end,
          },
        },
      },
      Title: {
        visible: true,
        text: { text },
        ...title,
      },
    });

    if (isUpdate) {
      this.tag('Timeline').currentTime = new Date();
    } else {
      this._goToLive();
    }
    this._refocus();
  }

  _handleGridX(offset, maxOffset) {
    this.tag('Timeline').setSmooth('x', offset);
    this.tag('Nav').percentage = offset / maxOffset;
  }

  _handleGridY(offset) {
    this.tag('Channels').setSmooth('y', offset);
  }

  _goToLive(animate = false) {
    const now = new Date();
    this.tag('Timeline').currentTime = now;
    this.tag('GridRoot').goToTimestamp(now, !animate);
  }

  _goLeft() {
    this.tag('GridRoot').goLeft();
  }

  _goRight() {
    this.tag('GridRoot').goRight();
  }

  _handleDown() {
    if (this._focusMode === FOCUS_MODE_GRID) {
      return false;
    }

    this._focusMode = FOCUS_MODE_GRID;

    return true;
  }

  _handleUp() {
    if (this._focusMode === FOCUS_MODE_NAV) {
      return false;
    }

    this._focusMode = FOCUS_MODE_NAV;

    return true;
  }

  _handleBoxSelected(content) {
    if (content.action) {
      if (!isActionAllowed(content.action)) {
        return;
      }

      if (content.action.target.type && content.action.target.value_modal) {
        this._openEpgModal(content.action.target.value_modal.id);

        return;
      }

      this.signal(SELECTED_SIGNAL, { id: `${this._row}_${this._index}`, ...content });
    } else {
      this.fireAncestors(`$${NOTIFICATION_SIGNAL}`, makeNotification(NOTIFICATION_LEVEL.SYSTEM, t('epgGrid.noAction')));
    }
  }

  _getFocused() {
    return this._focusMode === FOCUS_MODE_NAV ? this.tag('Nav') : this.tag('GridRoot');
  }

  _handleGoToLive() {
    const now = new Date();
    if (now >= this._start && now <= this._end) {
      this._goToLive(true);

      return;
    }

    this.signal(EPG_SIGNAL, {
      date: new Date(),
      blockId: this._data.block.id,
    });
  }

  _openDateSelector() {
    Store.dispatch(
      showPopupAction(EPG_POPUP_ID, {
        date: new Date(),
        selectedDate: this._start,
        blockId: this._data.block.id,
      }),
    );
  }

  _openEpgModal(value) {
    if (!value) {
      return;
    }

    Store.dispatch(
      showPopupAction(EPG_LAYOUT_POPUP_ID, {
        blockId: value.toString(),
      }),
    );
  }

  _goToHour(hour) {
    this.tag('GridRoot').goToTimestamp(offsetFromStart(this._start.getTime(), hour), false, false);
  }

  _goToMorning() {
    this._goToHour(MORNING_HOUR);
  }

  _goToAfternoon() {
    this._goToHour(AFTERNOON_HOUR);
  }

  _goToEvening() {
    this._goToHour(EVENING_HOUR);
  }
}

HorizontalEpg.Templates = templates;

export default HorizontalEpg;
